import type { ReactNode } from "react";
import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface RadioOption {
  value: string;
  label: ReactNode;
  description?: string;
  disabled?: boolean
  children?: ReactNode;
}

interface RadioGroupProps<TFieldValues extends FieldValues = FieldValues> {
  /** The field name in the form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Array of radio button options */
  options: RadioOption[];
  /** Validation rules for the field */
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  /** Whether the radio group is disabled */
  disabled?: boolean;
  /** Whether to disable the border around radio options */
  hideBorder?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Layout direction for the radio buttons
   * - "horizontal": Radio buttons are displayed side by side (default)
   * - "vertical": Radio buttons are stacked vertically
   * When horizontal, each option will take equal space (flex-fill)
   */
  layout?: "horizontal" | "vertical";
  /**
   * Visual variant of the radio group
   * - "card": Card-style with visible radio button and label (default)
   * - "button": Simple button-style without visible radio button
   */
  variant?: "card" | "button";
}

export default function RadioGroup<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  options,
  rules,
  disabled,
  hideBorder = false,
  className = "",
  layout = "horizontal",
  variant = "card",
}: RadioGroupProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  const containerClass = layout === "horizontal" ? "d-flex gap-2" : "d-flex flex-column gap-3";

  // Button variant styling
  if (variant === "button") {
    return (
      <div className={className}>
        <div className={containerClass}>
          {options.map((option) => {
            const optionId = `${name}-${option.value}`;
            const isChecked = field.value === option.value;
            const isDisabled = disabled || option.disabled;
            return (
              <div
                key={option.value}
                style={{
                  position: "relative",
                  flex: layout === "horizontal" ? 1 : undefined,
                }}
              >
                <input
                  type="radio"
                  id={optionId}
                  name={field.name}
                  value={option.value}
                  checked={isChecked}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  disabled={isDisabled}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    width: 0,
                    height: 0,
                  }}
                />
                <label
                  htmlFor={optionId}
                  style={{
                    display: "block",
                    padding: "8px 16px",
                    border: "1px solid #dfdfdf",
                    borderRadius: "4px",
                    backgroundColor: isChecked ? "#F5F9FE" : "#FFFFFF",
                    borderColor: isChecked ? "#2C6ECA" : "#dfdfdf",
                    color: "#212529",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: 400,
                    opacity: isDisabled ? 0.6 : 1,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled && !isChecked) {
                      e.currentTarget.style.backgroundColor = "#F8F9FA";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled && !isChecked) {
                      e.currentTarget.style.backgroundColor = "#FFFFFF";
                    }
                  }}
                >
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
        {error && (
          <div className="invalid-feedback d-block">
            {error.message ? t(error.message) : ""}
          </div>
        )}
      </div>
    );
  }

  // Card variant (default)
  return (
    <div className={className}>
      <div className={containerClass}>
        {options.map((option) => {
          const optionId = `${name}-${option.value}`;
          const isChecked = field.value === option.value;
          const isDisabled = disabled || option.disabled;
          return (
            <div
              key={option.value}
              className={`${hideBorder ? "" : "border less-rounded"} ${hideBorder ? "" : "p-3"} ${
                isChecked && !hideBorder ? "border-primary" : ""
              } ${layout === "horizontal" ? "flex-fill" : ""}`}
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                backgroundColor: "#FFFFFF",
                borderColor: hideBorder ? "transparent" : isChecked ? undefined : "#dfdfdf",
                borderRadius: hideBorder ? 0 : "8px",
                flex: layout === "horizontal" ? 1 : undefined,
                opacity: isDisabled ? 0.6 : 1,
              }}
              onClick={() => !isDisabled && field.onChange(option.value)}
            >
              <div className="d-flex flex-column gap-1">
                <div className="d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={optionId}
                    name={field.name}
                    value={option.value}
                    checked={isChecked}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    disabled={isDisabled}
                    style={{ marginTop: 0 }}
                  />
                  <label
                    className="form-check-label mb-0 fw-semibold"
                    htmlFor={optionId}
                    style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                  >
                    {option.label}
                  </label>
                </div>
                {option.description && (
                  <p
                    className="text-muted mb-0 ms-4"
                    style={{ fontSize: "14px" }}
                  >
                    {option.description}
                  </p>
                )}
              </div>
              {option.children && (
                option.children
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}

